import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { brandHex } from "./theme";
import { Panel, LoadingRows, ErrorState, EmptyState } from "./Panel";
import type { OccupancyData, AnalyticsViewProps } from "./types";

function hourLabel(iso: string): string {
  return iso.slice(11, 16); // "HH:MM"
}

export function OccupancyHeatmapView({
  data,
  loading,
  error,
}: AnalyticsViewProps<OccupancyData>) {
  const { t } = useTranslation();
  const zones = data?.zones ?? [];
  const buckets = data?.buckets ?? [];
  const max = Math.max(1, ...buckets.flatMap((b) => Object.values(b.counts)));
  // brandHex() is read once here and this component does not subscribe to theme
  // changes — fine today because the brand accent is theme-independent; revisit
  // if a theme-dependent brand token is introduced.
  const accent = brandHex();

  return (
    <Panel title={t("homework.analytics.occupancy.title")}>
      {loading ? (
        <LoadingRows />
      ) : error ? (
        <ErrorState message={t("homework.analytics.error")} />
      ) : !zones.length || !buckets.length ? (
        <EmptyState message={t("homework.analytics.empty")} />
      ) : (
        <div className="overflow-x-auto">
          <div
            className="inline-grid gap-0.5"
            style={{ gridTemplateColumns: `minmax(80px,auto) repeat(${buckets.length}, 20px)` }}
          >
            <div />
            {buckets.map((b) => (
              <div key={b.hour} className="text-[9px] text-muted-foreground -rotate-45 text-center">
                {hourLabel(b.hour)}
              </div>
            ))}
            {zones.map((z) => (
              <Fragment key={z.zone_id}>
                <div className="pr-2 text-[12px] truncate">{z.name}</div>
                {buckets.map((b) => {
                  const n = b.counts[z.zone_id] ?? 0;
                  return (
                    <div
                      key={z.zone_id + b.hour}
                      title={`${z.name} ${hourLabel(b.hour)}: ${n}`}
                      className="h-5 w-5 rounded-[2px]"
                      style={{ backgroundColor: accent, opacity: n ? 0.15 + 0.85 * (n / max) : 0.05 }}
                    />
                  );
                })}
              </Fragment>
            ))}
          </div>
        </div>
      )}
      {data?.caveat && <p className="mt-2 text-[11px] text-muted-foreground">{data.caveat}</p>}
    </Panel>
  );
}
